import { TransactionApiUrls } from "@/modules/transaction/common/enum";
import type {
  IAccountTransfer,
  ICreateAccountTransferRequest,
  ICreateOwnAccountTransferRequest,
  IOwnAccountsTransfer,
  ISaveAsTemplate,
  IUpdateAccountTransferRequest,
  IUpdateOwnAccountTransferRequest,
} from "@/modules/transaction/common/types";
import type { ICommission, IObject } from "@/shared/common/types";
import { generateQuery, withIdempotency } from "@/shared/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const accountTransferApi = createApi({
  reducerPath: "accountTransferApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    createAccountTransfer: builder.mutation<string, ICreateAccountTransferRequest>(
      withIdempotency<ICreateAccountTransferRequest>(
        TransactionApiUrls.AccountTransferV2
      )),
    updateAccountTransfer: builder.mutation<string, IUpdateAccountTransferRequest>({
      query: ({ data, id }) => ({
        url: `${TransactionApiUrls.AccountTransferV2}/${id}`,
        method: "PUT",
        data,
      }),
    }),
    getAccountTransfer: builder.query<IAccountTransfer, {
      id: string
    }>({
      query: ({ id }) => ({
        url: `${TransactionApiUrls.AccountTransfer}/${id}`,
      }),
      keepUnusedDataFor: 0,
    }),
    getOwnTransfer: builder.query<IOwnAccountsTransfer, {
      id: string
    }>({
      query: ({ id }) => ({
        url: `${TransactionApiUrls.OwnAccountTransfer}/${id}`,
      }),
    }),
    createOwnTransfer: builder.mutation<string, ICreateOwnAccountTransferRequest>(
      withIdempotency<ICreateOwnAccountTransferRequest>(
        TransactionApiUrls.OwnAccountTransferV2
      )),
    updateOwnTransfer: builder.mutation<string, IUpdateOwnAccountTransferRequest>({
      query: ({ data, id }) => ({
        url: `${TransactionApiUrls.OwnAccountTransferV2}/${id}`,
        method: "PUT",
        data,
      }),
    }),
    getAccountTransferCommission: builder.query<ICommission, IObject>({
      query: (data) => {
        const query = generateQuery(data);
        return ({
          url: `${TransactionApiUrls.AccountTransferCommission}?${query}`,
        });
      },
    }),
    saveAccountTransferAsTemplate: builder.mutation<string, ISaveAsTemplate>({
      query: ({ id, name, idempotencyKey }) => ({
        url: `${TransactionApiUrls.AccountTransfer}/${id}/Templates`,
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        data: { name },
      }),
    }),
    saveOwnAccountTransferAsTemplate: builder.mutation<string, ISaveAsTemplate>({
      query: ({ id, name, idempotencyKey }) => ({
        url: `${TransactionApiUrls.OwnAccountTransfer}/${id}/Templates`,
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        data: { name },
      }),
    }),
  }),
});

export const {
  useCreateAccountTransferMutation,
  useUpdateAccountTransferMutation,
  useCreateOwnTransferMutation,
  useGetAccountTransferQuery,
  useGetOwnTransferQuery,
  useUpdateOwnTransferMutation,
  useLazyGetAccountTransferCommissionQuery,
  useSaveAccountTransferAsTemplateMutation,
  useSaveOwnAccountTransferAsTemplateMutation,
} = accountTransferApi;
